
import React, { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Heart, Award, Users, Camera, Snowflake, Baby, Utensils } from "lucide-react";
import InteractiveCard from "./InteractiveCard";
import './About.css';
import { colors } from "../styles/colors";
import { useContent } from "../cms/ContentContext";
import { PrimaryButton, SecondaryButton, OutlineButton } from './ui/StandardButton';
import HeroHeading from "./ui/HeroHeading";
import { useResponsive } from "../hooks/useResponsive";
import SeoAlternateLinks from "./seo/SeoAlternateLinks";
import { buildAbsoluteUrl, derivePageMeta } from "../utils/seo";
import LazyVideo from "./LazyVideo";

const values = [
	{
		icon: <Heart className="h-6 w-6" />,
		titleKey: "about.values.family.title",
		descKey: "about.values.family.description"
	},
	{
		icon: <Award className="h-6 w-6" />,
		titleKey: "about.values.authentic.title",
		descKey: "about.values.authentic.description"
	},
	{
		icon: <Users className="h-6 w-6" />,
		titleKey: "about.values.groups.title",
		descKey: "about.values.groups.description"
	},
	{
		icon: <Camera className="h-6 w-6" />,
		titleKey: "about.values.memories.title",
		descKey: "about.values.memories.description"
	}
];

const timeline = [
	{
		year: "1990s",
		titleKey: "about.timeline.childhood.title",
		descKey: "about.timeline.childhood.description",
		icon: <Baby className="h-6 w-6" />,
		image: "/images/Nya_bilder/gustav_childhood.webp"
	},
	{
		year: "2000s",
		titleKey: "about.timeline.culinary.title",
		descKey: "about.timeline.culinary.description",
		icon: <Utensils className="h-6 w-6" />,
		image: "/images/Nya_bilder/julias_matresa.webp"
	},
	{
		year: "2020",
		titleKey: "about.timeline.love.title",
		descKey: "about.timeline.love.description",
		icon: <Heart className="h-6 w-6" />,
		image: "/images/Nya_bilder/love_adventure.webp"
	},
	{
		year: "2022-2023",
		titleKey: "about.timeline.family.title",
		descKey: "about.timeline.family.description",
		icon: <Users className="h-6 w-6" />,
		image: "/images/Nya_bilder/family_2.webp"
	},
	{
		year: "2024",
		titleKey: "about.timeline.coldExperience.title",
		descKey: "about.timeline.coldExperience.description",
		icon: <Snowflake className="h-6 w-6" />,
		image: "/images/Nya_bilder/coldexperience_born.webp"
	}
];

const About = () => {
	const { t, i18n } = useTranslation();
	const { isMobile, isTablet } = useResponsive();
	const { getMedia } = useContent();
	const heroSpacing = isMobile ? "px-4 pt-24 pb-16" : "px-4 sm:px-6 lg:px-8 pt-32 pb-24";
	const meta = derivePageMeta({
		t,
		i18n,
		pageKey: "about",
		routeKey: "about",
		defaults: {
			ogImage: "/og/about.jpg",
		},
	});
	const ogImageUrl = buildAbsoluteUrl(meta.siteUrl, meta.ogImage);
	const twitterImageUrl = buildAbsoluteUrl(meta.siteUrl, meta.twitterImage);
	// Scroll-triggered animation for timeline cards
	const timelineRefs = useRef([]);
	useEffect(() => {
		const observer = new window.IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						entry.target.classList.add("timeline-card-visible");
					}
				});
			},
			{ threshold: 0.2 }
		);
		timelineRefs.current.forEach((el) => {
			if (el) observer.observe(el);
		});
		return () => observer.disconnect();
	}, []);

	return (
		<>
			<Helmet>
				{meta.title && <title>{meta.title}</title>}
				{meta.description && <meta name="description" content={meta.description} />}
				{meta.keywords && <meta name="keywords" content={meta.keywords} />}
				<link rel="canonical" href={meta.canonicalUrl} />
				<meta property="og:title" content={meta.ogTitle} />
				<meta property="og:description" content={meta.ogDescription} />
				<meta property="og:image" content={ogImageUrl} />
				<meta property="og:url" content={meta.canonicalUrl} />
				<meta name="twitter:image" content={twitterImageUrl} />
				{meta.jsonLd && (
					<script type="application/ld+json">{JSON.stringify(meta.jsonLd)}</script>
				)}
			</Helmet>
			<SeoAlternateLinks
				routeKey="about"
				canonicalPath={meta.canonicalPath}
				siteUrl={meta.siteUrl}
			/>

			{/* Hero Section with Background GIF and Video */}
			<section
				id="about-hero"
				data-cms-page="about"
				data-cms-section="aboutHero"
				data-hero="true"
				className="relative min-h-screen flex items-center justify-center overflow-hidden"
				style={{ background: colors.gradients.hero }}
			>
				{/* Background Media */}
				<div className="absolute inset-0 overflow-hidden">
					<LazyVideo
						src={getMedia('about.hero.video', '/optimized_videos/om_oss-opt.mp4')}
						poster={getMedia('about.hero.poster', '/images/Nya_bilder/IMG_2425.webp')}
						autoPlay
						loop
						muted
						playsInline
						preload={isMobile ? "none" : "metadata"}
						className="absolute inset-0"
						videoClassName="absolute inset-0 w-full h-full object-cover"
					/>
					<div className="absolute inset-0" style={{ background: colors.gradients.hero }}></div>
				</div>

				<div className={`relative z-10 w-full ${heroSpacing}`}>
					<div
						className={`${isMobile ? 'max-w-sm' : 'max-w-4xl'} mx-auto text-center`}
						style={{ color: colors.text.primary }}
					>
						<HeroHeading
							title={t("about.title")}
							stacked
							className={`mb-6 ${isMobile ? 'text-3xl' : isTablet ? 'text-4xl' : 'text-5xl md:text-6xl'
								}`}
						/>
						<div className={isMobile ? 'max-w-full' : 'max-w-3xl mx-auto'}>
							<p
								className={`leading-relaxed ${isMobile ? 'text-lg' : 'text-xl md:text-2xl'
									}`}
							>
								{t("about.intro")}
							</p>
						</div>
					</div>
				</div>
			</section>

			<main
				className="min-h-screen relative"
				style={{ background: colors.gradients.heroSolid }}
			>
				<div className={`relative z-10 mx-auto py-20 ${isMobile ? 'max-w-sm px-4' : 'max-w-7xl px-4 sm:px-6 lg:px-8'
					}`}>

					{/* Values Section */}
					<section id="about-values" data-cms-page="about" data-cms-section="values" className={isMobile ? 'mb-12' : 'mb-16'}>
						<h2 className={`font-bold text-center mb-12 ${isMobile ? 'text-2xl' : 'text-3xl'
							}`} style={{ color: colors.text.primary }}>
							{t("about.valuesTitle")}
						</h2>
						<div className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4 sm:gap-8'
							}`}>
							{values.map((value, index) => {
								const backgroundKeys = [
									'about.values.family.image',
									'about.values.authentic.image',
									'about.values.groups.image',
									'about.values.memories.image'
								];
								const backgroundImages = [
									"/images/Nya_bilder/IMG_2425.webp", // Familj
									"/images/Nya_bilder/IMG_7834.webp", // Äkta
									"/images/Nya_bilder/IMG_1547.webp", // Grupper
									"/images/Nya_bilder/IMG_4436.webp"  // Minnen
								];

								return (
									<InteractiveCard
										key={index}
										variant="glass"
										className="relative text-center overflow-hidden"
									>
										{/* Background Image */}
										<div
											className="absolute inset-0 bg-cover bg-center opacity-60"
											style={{ backgroundImage: `url(${getMedia(backgroundKeys[index], backgroundImages[index])})` }}
										></div>
										<div className="relative z-10 p-6 sm:p-8">
											<h3
												className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 font-bold drop-shadow-lg"
												style={{ color: colors.text.primary }}
											>
												{t(value.titleKey)}
											</h3>
											<p
												className="text-sm sm:text-base leading-relaxed drop-shadow-md"
												style={{ color: colors.text.primary }}
											>
												{t(value.descKey)}
											</p>
										</div>
									</InteractiveCard>
								);
							})}
						</div>
					</section>

					{/* Personal Section - Meet Gustav & Julia */}
					<section id="about-meet-us" data-cms-page="about" data-cms-section="meetUs" className={isMobile ? 'mb-12' : 'mb-20'}>
						<div className={`items-center mb-12 ${isMobile ? 'space-y-8' : 'grid md:grid-cols-2 gap-12'
							}`}>
							<div>
								<h2 className={`font-bold mb-6 ${isMobile ? 'text-2xl' : 'text-3xl'
									}`} style={{ color: colors.text.primary }}>
									{t("about.meetUs.title")}
								</h2>
								<p className={`mb-6 ${isMobile ? 'text-base' : 'text-lg'
									}`} style={{ color: colors.text.primary }}>
									{t("about.meetUs.description")}
								</p>
								<p className={`mb-8 ${isMobile ? 'text-base' : 'text-lg'
									}`} style={{ color: colors.text.primary }}>
									{t("about.meetUs.experience")}
								</p>

								<div className={`gap-4 ${isMobile ? 'grid grid-cols-1 space-y-4' : 'grid grid-cols-1 md:grid-cols-2'
									}`}>
									<InteractiveCard className="p-4" variant="glass">
										<div className="flex items-center space-x-3">
											<div
												className="flex-shrink-0 p-2 rounded-lg"
												style={{ backgroundColor: colors.accent.primary + '20' }}
											>
												<Users className="h-6 w-6" style={{ color: colors.accent.primary }} />
											</div>
											<div>
												<h3 className="font-semibold mb-1" style={{ color: colors.text.primary }}>
													{t("about.meetUs.guides")}
												</h3>
												<p className="text-sm" style={{ color: colors.text.primary }}>
													{t("about.meetUs.guidesDesc")}
												</p>
											</div>
										</div>
									</InteractiveCard>
									<InteractiveCard className="p-4" variant="glass">
										<div className="flex items-center space-x-3">
											<div
												className="flex-shrink-0 p-2 rounded-lg"
												style={{ backgroundColor: colors.accent.primary + '20' }}
											>
												<Heart className="h-6 w-6" style={{ color: colors.accent.primary }} />
											</div>
											<div>
												<h3 className="font-semibold mb-1" style={{ color: colors.text.primary }}>
													{t("about.meetUs.family")}
												</h3>
												<p className="text-sm" style={{ color: colors.text.primary }}>
													{t("about.meetUs.familyDesc")}
												</p>
											</div>
										</div>
									</InteractiveCard>
								</div>
							</div>

							<div className={`relative ${isMobile ? 'mt-8' : ''}`}>
								<img
									src={getMedia('ownerSection.ownerImageUrl', '/images/owners.webp')}
									alt="Gustav och Julia - Cold Experience"
									className={`rounded-lg shadow-xl w-full ${isMobile ? 'h-64 object-cover' : ''
										}`}
									loading={isMobile ? "lazy" : "eager"}
								/>
								<div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-lg"></div>
							</div>
						</div>

						{/* Action Images Grid */}
						<div className={`gap-6 ${isMobile ? 'grid grid-cols-1 mt-8' : 'grid md:grid-cols-3'
							}`}>
							<div className="relative">
								<img
									src={getMedia('about.actionImages.snowmobile', '/images/Nya_bilder/IMG_4108.webp')}
									alt={t("about.actionImages.snowmobile")}
									className="rounded-lg shadow-lg w-full h-64 object-cover"
								/>
								<div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-lg flex items-end">
									<p className="text-white font-semibold p-4">{t("about.actionImages.snowmobile")}</p>
								</div>
							</div>
							<div className="relative">
								<img
									src={getMedia('about.actionImages.lodge', '/images/Nya_bilder/IMG_3493.webp')}
									alt={t("about.actionImages.lodge")}
									className="rounded-lg shadow-lg w-full h-64 object-cover"
								/>
								<div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-lg flex items-end">
									<p className="text-white font-semibold p-4">{t("about.actionImages.lodge")}</p>
								</div>
							</div>
							<div className="relative">
								<img
									src={getMedia('about.actionImages.landscape', '/images/Nya_bilder/IMG_6698.webp')}
									alt={t("about.actionImages.landscape")}
									className="rounded-lg shadow-lg w-full h-64 object-cover"
								/>
								<div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-lg flex items-end">
									<p className="text-white font-semibold p-4">{t("about.actionImages.landscape")}</p>
								</div>
							</div>
						</div>
					</section>

					{/* Timeline Section - Modern Vertical Timeline with icons and animation */}
					<section id="about-timeline" data-cms-page="about" data-cms-section="journey" className={`relative ${isMobile ? 'mb-12' : 'mb-20'}`}>
						<div className="relative z-10">
							<h2 className={`font-bold text-center ${isMobile ? 'text-2xl mb-8' : 'text-3xl sm:text-4xl mb-10 sm:mb-16'
								}`} style={{ color: colors.text.primary }}>
								{t("about.journeyTitle")}
							</h2>
							<div className={`relative timeline ${isMobile
								? 'border-l-2 pl-8'
								: 'border-l-4 pl-16'
								}`} style={{ borderColor: colors.accent.primary + '60' }}>
								{timeline.map((item, index) => (
									<div
										key={index}
										ref={el => timelineRefs.current[index] = el}
										className={`flex items-start timeline-card relative ${isMobile ? 'mb-12' : 'mb-16'
											}`}
									>
										<div className={`absolute top-8 ${isMobile ? '-left-4' : '-left-6'
											}`}>
											<div
												className={`rounded-full flex items-center justify-center shadow-lg backdrop-blur-sm ${isMobile ? 'w-6 h-6' : 'w-8 h-8'
													}`}
												style={{
													backgroundColor: colors.accent.primary,
													border: `2px solid ${colors.text.primary}`
												}}
											>
											</div>
										</div>
										<InteractiveCard
											variant="glass"
											className={`w-full timeline-card-content overflow-hidden ${isMobile ? 'ml-4 max-w-full' : 'ml-8 max-w-2xl'
												}`}
										>
											<div className={isMobile ? 'flex flex-col' : 'flex flex-col md:flex-row'}>
												{/* Timeline Image */}
												<div className={`relative overflow-hidden ${isMobile
													? 'w-full h-48'
													: 'w-full md:w-1/2 h-64 md:h-80'
													}`}>
													<img
														src={getMedia(`about.timeline.${item.titleKey.split('.')[2]}.image`, item.image)}
														alt={t(item.titleKey)}
														className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
														loading={isMobile ? "lazy" : "eager"}
													/>
													<div className="absolute inset-0 bg-gradient-to-r md:bg-gradient-to-t from-black/20 to-transparent"></div>
												</div>

												{/* Timeline Content */}
												<div className={`${isMobile
													? 'w-full p-4 flex flex-col justify-center'
													: 'w-full md:w-1/2 p-6 flex flex-col justify-center'
													}`}>
													<div className={`flex items-center gap-3 mb-4 ${isMobile ? 'justify-center' : ''
														}`}>
														<div
															className={`rounded-full ${isMobile ? 'p-1.5' : 'p-2'
																}`}
															style={{
																backgroundColor: colors.accent.primary + '20',
																color: colors.accent.primary
															}}
														>
															{item.icon}
														</div>
														<span
															className={`font-bold px-4 py-2 rounded-full ${isMobile ? 'text-sm' : 'text-lg'
																}`}
															style={{
																backgroundColor: colors.accent.primary + '60',
																color: colors.text.primary
															}}
														>
															{item.year}
														</span>
													</div>
													<h3 className={`font-bold mb-4 ${isMobile ? 'text-lg text-center' : 'text-xl'
														}`} style={{ color: colors.text.primary }}>
														{t(item.titleKey)}
													</h3>
													<p className={`leading-relaxed ${isMobile ? 'text-sm text-center' : 'text-base'
														}`} style={{ color: colors.text.primary }}>
														{t(item.descKey)}
													</p>
												</div>
											</div>
										</InteractiveCard>
									</div>
								))}
							</div>
						</div>
					</section>

					{/* Call to Action */}
					<section
						id="about-cta"
						data-cms-page="about"
						data-cms-section="cta"
						className={`relative text-center overflow-hidden ${isMobile ? 'py-12' : 'py-16'
							}`}
						style={{ backgroundColor: colors.secondary }}
					>
						{/* Background Image */}
						<div
							className="absolute inset-0 bg-cover bg-center opacity-30"
							style={{ backgroundImage: `url(${getMedia('about.cta.backgroundImage', '/images/Nya_bilder/IMG_0451.webp')})` }}
						></div>

						<div className={`relative z-10 mx-auto ${isMobile ? 'max-w-sm px-4' : 'max-w-4xl px-4'
							}`}>
							<h2 className={`font-bold mb-6 ${isMobile ? 'text-2xl' : 'text-3xl'
								}`} style={{ color: colors.text.primary }}>
								{t("about.cta.title")}
							</h2>
							<p className={`mb-8 ${isMobile ? 'text-lg' : 'text-xl'
								}`} style={{ color: colors.text.primary }}>
								{t("about.cta.description")}
							</p>
							<div className={`gap-4 justify-center ${isMobile ? 'flex flex-col' : 'flex flex-col sm:flex-row'
								}`}>
								<PrimaryButton href="/sv/paketresor">
									{t("about.cta.packages")}
								</PrimaryButton>
								<SecondaryButton href="/sv/contact">
									{t("about.cta.contact")}
								</SecondaryButton>
								<OutlineButton href="/sv/gallery">
									{t("about.cta.gallery")}
								</OutlineButton>
							</div>
						</div>
					</section>
				</div>
			</main>
			{/* Footer removed, now rendered globally in App.js */}
		</>
	);
};

export default About;
